import {
  EventEnrollNextStatus,
  EventEntryEnroll,
  StatusInterface,
  SurveyAnswerValueInterface,
} from '@elearning-platform/api-types/interfaces';
import { Color, IconName, useNotifications, UseNotifications } from '@elearning-platform/ui';
import { QueryClient, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { TransProps, useTranslation } from 'react-i18next';

import { enrollEvent, sendQuestion } from '../api/requests';
import { QueryKey, RouteNameEnum } from '../enums';
import {
  EnrollEventPayload,
  FormAnswers,
  SaveQuestionPayload,
  UseAuth,
  UseEventEnrollmentSubmit,
  UseEventEnrollmentSubmitParams,
  UseRedirectTo,
} from '../interfaces';
import { mapFormValuesToAnswers } from '../utils';

import { useAuth } from './useAuth.hook';
import { useRedirectTo } from './useRedirectTo.hook';

export const useEventEnrollmentSubmit: (params: UseEventEnrollmentSubmitParams) => UseEventEnrollmentSubmit
= (params: UseEventEnrollmentSubmitParams): UseEventEnrollmentSubmit => {
  const queryClient: QueryClient = useQueryClient();
  const { userData }: UseAuth = useAuth();
  const { addToast, showPopup, hidePopup }: UseNotifications = useNotifications();
  const { t }: TransProps<never> = useTranslation();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const attemptId: string | undefined = params.attemptId;

  const {
    mutate: enroll,
    isPending: isEnrollPending,
  }: UseMutationResult<EventEnrollNextStatus, Error, EnrollEventPayload> = useMutation<EventEnrollNextStatus, Error, EnrollEventPayload>({
    mutationFn: ({ eventId, data }: EnrollEventPayload): Promise<EventEnrollNextStatus> => enrollEvent(eventId, data),
  });

  const {
    mutate: saveQuestion,
    isPending: isSaveQuestionPending,
  }: UseMutationResult<StatusInterface, Error, SaveQuestionPayload> = useMutation({
    mutationFn: ({ attemptId, answers }: SaveQuestionPayload): Promise<StatusInterface> => sendQuestion(attemptId, answers),
  });

  const onSuccess: () => void = (): void => {
    addToast({
      text: params.attemptId
        ? t('competence.app.eventEnroll.editSuccess')
        : t('competence.app.eventEnroll.enrollmentSuccess'),
      icon: IconName.CheckmarkCircle,
      iconColor: Color.Green300,
    });

    if (params.id) {
      redirect({ route: RouteNameEnum.TrainingDetails, params: { id: params.id.toString() } });
    } else {
      redirect({ route: RouteNameEnum.Trainings });
    }

    void queryClient.removeQueries({ queryKey: [QueryKey.Event, params.id] });
  };

  const onError: (error: Error) => void = (error: Error): void => showPopup({
    description: error.message || t('competence.app.global.defaultErrorDescription'),
    title: t('competence.app.global.errorOccurred'),
    acceptButtonData: {
      label: t('competence.app.global.close'),
      onClick: (): void => hidePopup(),
    },
  });

  const onSubmit: (data: FormAnswers) => void = (data: FormAnswers): void => {
    if (attemptId && params.enrollSurvey) {
      const answers: SurveyAnswerValueInterface[] = mapFormValuesToAnswers(params.enrollSurvey, {
        supervisorName: data.supervisorData.name,
        supervisorSurname: data.supervisorData.surname,
        supervisorEmail: data.supervisorData.email,
        phone: data.userData.phone,
        accommodation: data.userData.addons.withAccommodation,
        parking: data.userData.addons.withParking,
        vegeFood: data.userData.addons.withVege,
        dinner: data.userData.addons.withDinner,
      });

      saveQuestion(
        { attemptId: Number(attemptId), answers },
        { onSuccess, onError },
      );

      return;
    }

    const formData: EventEntryEnroll = {
      supervisor: {
        name: data.supervisorData.name,
        surname: data.supervisorData.surname,
        email: data.supervisorData.email,
      },
      user: {
        email: userData?.email || '',
        phone: data.userData.phone,
        accommodation: data.userData.addons.withAccommodation,
        parking: data.userData.addons.withParking,
        vege: data.userData.addons.withVege,
        dinner: data.userData.addons.withDinner,
      },
    };

    enroll(
      { eventId: Number(params.id), data: formData },
      { onSuccess, onError },
    );
  };

  return {
    handleEnrollSubmit: onSubmit,
    isSubmitting: isEnrollPending || isSaveQuestionPending,
  };
};
